import {
  createMigrate,
  type MigrationManifest,
  type PersistedState,
} from 'redux-persist';

/**
 * Migration key in localStorage to track if user was notified about v1→v2 migration
 */
export const MIGRATION_NOTIFICATION_KEY = 'persist:migration:v1-to-v2-notified';

/**
 * Check if user needs to be notified about v1→v2 migration.
 * Returns true if:
 * - User had v1 state in localStorage
 * - User hasn't been notified yet
 *
 * @returns true if notification should be shown
 */
export const shouldShowMigrationNotification = (): boolean => {
  // Check if notification was already shown
  const notificationShown = localStorage.getItem(MIGRATION_NOTIFICATION_KEY);
  if (notificationShown === 'true') {
    return false;
  }

  // Check if user had v1 state (by checking for persist:root key with version 1 or no version)
  const persistedState = localStorage.getItem('persist:root');
  if (!persistedState) {
    // New user, no state - mark as notified so we never show notification
    localStorage.setItem(MIGRATION_NOTIFICATION_KEY, 'true');
    return false;
  }

  try {
    const parsed = JSON.parse(persistedState);
    const version = parsed._persist?.version;

    // If version is 1 or undefined (old state), user needs notification
    if (version === undefined || version === 1) {
      return true;
    }

    // Version 2 or higher - mark as notified
    localStorage.setItem(MIGRATION_NOTIFICATION_KEY, 'true');
    return false;
  } catch {
    // If parsing fails, assume new user
    localStorage.setItem(MIGRATION_NOTIFICATION_KEY, 'true');
    return false;
  }
};

/**
 * Mark that migration notification was shown to user
 */
export const markMigrationNotificationShown = (): void => {
  localStorage.setItem(MIGRATION_NOTIFICATION_KEY, 'true');
};

/**
 * Migration manifest for redux-persist.
 * Each key is a version number, and the function transforms state FROM the previous version TO that version.
 * These migrations ONLY run when the persisted version is LESS than the target version.
 */
const migrationManifest: MigrationManifest = {
  // Migration from v1 to v2: Reset layout state to use fresh default template
  // This fixes ID mismatch issues when users had old state with different widget IDs
  // This ONLY runs for users upgrading from v1 (or no version) to v2
  2: state => {
    if (state && typeof state === 'object') {
      const {
        sections: _sections,
        blocks: _blocks,
        widgetInstances: _widgetInstances,
        regions: _regions,
        template: _template,
        ...rest
      } = state as Record<string, unknown>;
      return rest as PersistedState;
    }
    return state;
  },
};

/**
 * Create the migration function using redux-persist's createMigrate.
 * This ensures migrations only run when the persisted version is less than the current version,
 * NOT on every rehydration.
 */
export const migrate = createMigrate(migrationManifest, { debug: false });
